<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import BasicButton from '@/components/BasicButton.vue'
import SpinnerBasic from '@/components/SpinnerBasic.vue'
import FormError from '@/components/FormError.vue'
import { useI18n } from 'vue-i18n'
import { ArrowGotoIcon } from '@/assets'
import { Form, type GenericObject } from 'vee-validate'
import * as v from 'valibot'
import { toTypedSchema } from '@vee-validate/valibot'
import FormTextInput from '@/components/FormTextInput.vue'
import LinkButton from '@/components/LinkButton.vue'

const authStore = useAuthStore()
const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    email: string
  }>(),
  {
    email: '',
  },
)

const emits = defineEmits<{
  (e: 'close'): void
}>()

const currentStep = ref(0)
const step1Schema = v.object({
  email: v.pipe(
    v.string(),
    v.email(() => t('forms.errors.invalidEmail')),
    v.nonEmpty('required'),
  ),
})
const step2Schema = v.pipe(
  v.object({
    email: v.pipe(
      v.string(),
      v.nonEmpty(() => t('forms.errors.requiredField')),
      v.email(() => t('forms.errors.invalidEmail')),
    ),
    newPassword: v.pipe(
      v.string(),
      v.nonEmpty(() => t('forms.errors.requiredField')),
      v.minLength(8, () => t('forms.errors.passwordMinLength', { min: 8 })),
      v.regex(/[a-z]/, () => t('forms.errors.passwordLowercaseRequired')),
      v.regex(/[A-Z]/, () => t('forms.errors.paswordUppercaseRequired')),
      v.regex(/\d/, () => t('forms.errors.passwordNumberRequired')),
    ),
    repeatNewPassword: v.pipe(
      v.string(),
      v.nonEmpty(() => t('forms.errors.requiredField')),
    ),
    confirmationCode: v.pipe(
      v.string(),
      v.nonEmpty(() => t('forms.errors.requiredField')),
    ),
  }),
  v.forward(
    v.partialCheck(
      [['newPassword'], ['repeatNewPassword']],
      (input) => input.newPassword === input.repeatNewPassword,
      () => t('forms.errors.passwordsDoNotMatch'),
    ),
    ['repeatNewPassword'],
  ),
)

const schemas = [toTypedSchema(step1Schema), toTypedSchema(step2Schema)]
const currentSchema = computed(() => {
  return schemas[currentStep.value]
})

const isError = ref(false)
const errorMessage = ref<() => string>(() => '')
const passwordWasReset = ref(false)

async function handlePasswordReset(email: string): Promise<boolean> {
  isError.value = false
  try {
    const result = await authStore.resetPassword({ username: email })
    switch (result) {
      case 'done':
        passwordWasReset.value = true
        return true
      case 'enterCodeEmail':
        return true
      case 'enterCodeSMS':
        return true
      case 'codeDeliveryFailure':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.codeDeliveryFailure')
        return false
      case 'forbidden':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.forbidden')
        return false
      case 'limitExceeded':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.limitExceeded')
        return false
      case 'tooManyRequests':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.tooManyRequests')
        return false
      case 'unexpectedError':
      default:
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.unexpectedError')
        return false
    }
  } catch (e) {
    console.error(e)
    isError.value = true
    errorMessage.value = () => t('authenticatorCard.error.passwordResetFailed')
    return false
  }
}

async function handlePasswordResetConfirmation(email: string, password: string, code: string): Promise<boolean> {
  isError.value = false
  try {
    const result = await authStore.confirmResetPassword({ username: email, newPassword: password, confirmationCode: code })
    switch (result) {
      case 'passwordReset':
        return true
      case 'codeMismatch':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.codeMismatch')
        return false
      case 'expiredCode':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.expiredCode')
        return false
      case 'forbidden':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.forbidden')
        return false
      case 'invalidPassword':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.invalidPassword')
        return false
      case 'limitExceeded':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.limitExceeded')
        return false
      case 'tooManyRequests':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.tooManyRequests')
        return false
      case 'tooManyFailedAttempts':
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.tooManyFailedAttempts')
        return false
      case 'unexpectedError':
      default:
        isError.value = true
        errorMessage.value = () => t('authenticatorCard.error.unexpectedError')
        return false
    }
  } catch (e) {
    console.error(e)
    isError.value = true
    errorMessage.value = () => t('authenticatorCard.error.passwordResetConfirmationFailed')
    return false
  }
}

async function nextStep(values: GenericObject) {
  const typedValues = values as v.InferOutput<typeof step1Schema> & v.InferOutput<typeof step2Schema>
  if (currentStep.value === 0) {
    if (await handlePasswordReset(typedValues.email)) {
      currentStep.value++
    }
  } else if (currentStep.value === 1) {
    if (await handlePasswordResetConfirmation(typedValues.email, typedValues.newPassword, typedValues.confirmationCode)) {
      passwordWasReset.value = true
    }
  }
}

function prevStep() {
  if (currentStep.value <= 0) {
    return
  }

  currentStep.value--
}

function resetAndClose() {
  emits('close')
  form.value.resetForm()
  isError.value = false
  errorMessage.value = () => ''
  currentStep.value = 0
  passwordWasReset.value = false
}

const form = ref()

onMounted(() => {
  // Dynamically set initial form values
  if (props.email && props.email !== '') {
    form.value.setValues({ email: props.email })
  }
})
</script>

<template>
  <template v-if="!passwordWasReset">
    <h1>{{ $t('authenticatorCard.resetPasswordTitle') }}</h1>

    <Form
      ref="form"
      class="authenticator-fields"
      @submit="nextStep"
      @invalid-submit="(e) => console.log('invalid submit', e)"
      :validation-schema="currentSchema"
      keep-values
      v-slot="{ handleSubmit, values, isSubmitting }"
    >
      <p class="description">{{ $t('authenticatorCard.resetPasswordDescription') }}</p>
      <FormTextInput name="email" type="email" autocomplete="username" :label="$t('common.email')" />
      <template v-if="currentStep === 1">
        <FormTextInput name="newPassword" type="password" autocomplete="new-password" multiple-errors :label="$t('common.newPassword')" />
        <FormTextInput name="repeatNewPassword" type="password" autocomplete="new-password" :label="$t('common.repeatNewPassword')" />
        <FormTextInput
          name="confirmationCode"
          type="num"
          :precision="0"
          :allow-negative="false"
          :allow-multiple-zeros="true"
          autocomplete="one-time-code"
          :label="$t('common.confirmationCode')"
        />
      </template>
      <FormError v-if="isError">{{ errorMessage() }}</FormError>
      <BasicButton class="submit-button" :disabled="isSubmitting" type="submit">
        <span v-if="!isSubmitting">{{ $t('authenticatorCard.resetPasswordConfirmAction') }}</span>
        <SpinnerBasic v-else size="sm" />
      </BasicButton>

      <template v-if="currentStep === 1">
        <h4 class="new-section">{{ $t('authenticatorCard.codeDidNotArrive') }}</h4>
        <LinkButton :icon="ArrowGotoIcon" :disabled="isSubmitting" @click="prevStep">
          {{ $t('authenticatorCard.resendCode') }}
        </LinkButton>
      </template>

      <h4 class="new-section-mini">{{ $t('authenticatorCard.doYouRememberPassword') }}</h4>
      <LinkButton :icon="ArrowGotoIcon" :disabled="isSubmitting" @click="resetAndClose">
        {{ $t('authenticatorCard.returnToLogin') }}
      </LinkButton>
    </Form>
  </template>
  <template v-else>
    <h1>{{ $t('authenticatorCard.passwordResetSuccessTitle') }}</h1>
    <p class="description">{{ $t('authenticatorCard.passwordResetSuccessDescription') }}</p>
    <LinkButton :icon="ArrowGotoIcon" @click="resetAndClose">
      {{ $t('authenticatorCard.returnToLogin') }}
    </LinkButton>
  </template>
</template>

<style lang="scss" scoped>
.authenticator-frame {
  max-width: 100%;
}

.authenticator-fields {
  margin-top: 40px;
  max-width: 100%;
  width: 400px;

  > * {
    margin-bottom: 16px;
  }
}

.submit-button {
  margin-top: 40px;
  display: block;
  width: 100%;
}

.description {
  margin-bottom: 40px;
}

.new-section {
  margin-top: 32px;
  margin-bottom: 16px;
}

.new-section-mini {
  margin-top: 32px;
  margin-bottom: 16px;
}

.password-requirements {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-top: 24px;
  margin-bottom: 16px;
}

.password-requirements-description {
  margin-bottom: 12px;
}
</style>
